<template>
  <div
    v-loading.lock="pageLoading"
    class="week-report-page wr-app"
    :class="{ 'wr-page--enter': pageEnterAnimating, 'wr-page--loading': pageLoading }"
    element-loading-text="加载周报数据…"
  >
    <!-- 顶栏：左 返回 + 标题 / 右 周期 -->
    <header class="wr-top-bar">
      <div class="wr-top-left">
        <button
          type="button"
          class="wr-back-btn"
          aria-label="返回"
          @click="goBack"
        >
          <i class="el-icon-arrow-left"></i>
        </button>
        <h1 class="wr-title">我的总结</h1>
      </div>
      <div class="wr-top-right">
        <div class="wr-date-pill wr-week-pill" title="选择一周（周一至周日）">
          <i class="el-icon-date wr-week-pill__icon"></i>
          <span class="wr-week-pill__label">{{ weekRangeLabel }}</span>
          <el-date-picker
            v-model="weekPickerValue"
            type="week"
            class="wr-week-picker"
            :picker-options="weekPickerOptions"
            :clearable="false"
            :append-to-body="true"
            popper-class="wr-week-picker-popper"
            placeholder=""
            @change="onWeekPickerChange"
          />
        </div>
      </div>
    </header>

    <div class="wr-content" :class="{ 'is-loading': pageLoading }">
      <!-- 第一行：KPI 卡片 -->
      <div class="wr-kpi-row">
        <div class="wr-card wr-card--hero">
          <div class="wr-card-head">
            <span>视频处理总数</span>
            <el-tooltip content="本周成功完成分析的视频条数" placement="top">
              <i class="el-icon-question wr-icon-hint"></i>
            </el-tooltip>
          </div>
          <div class="wr-hero-metric">
            <span class="wr-hero-num">{{ formatInt(effectiveStats.videoCount) }}</span>
            <span
              v-if="videoGrowPercent !== null"
              class="wr-grow"
              :class="{ down: videoGrowPercent < 0 }"
            >
              {{ videoGrowPercent >= 0 ? '↗' : '↘' }} 较上周
              {{ videoGrowPercent > 0 ? '+' : '' }}{{ videoGrowPercent }}%
            </span>
          </div>
        </div>

        <div class="wr-card wr-card--time-bundle">
          <div class="wr-card-head">节省时间</div>
          <div class="wr-time-bundle">
            <div class="wr-time-col">
              <div class="wr-time-label">累计（小时）</div>
              <div class="wr-time-value">{{ hoursSaved }}</div>
              <div class="wr-time-unit">小时</div>
            </div>
            <div class="wr-time-col">
              <div class="wr-time-label">日均（分钟）</div>
              <div class="wr-time-value">{{ avgDailyMinutes }}</div>
              <div class="wr-time-unit">分钟</div>
            </div>
            <div class="wr-time-col">
              <div class="wr-time-label">较上周</div>
              <div
                v-if="timeComparePercent !== null"
                class="wr-time-value wr-time-value--compare"
                :class="{ 'is-down': timeComparePercent < 0 }"
              >
                {{ timeComparePercent >= 0 ? '▲' : '▼' }} {{ Math.abs(timeComparePercent) }}%
              </div>
              <div v-else class="wr-time-value wr-time-value--empty">—</div>
              <div class="wr-time-unit">环比</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 第二行：处理状态 + 标签分布 -->
      <div class="wr-charts-row">
        <div class="wr-card wr-card--chart wr-card--tag-pie">
          <div class="wr-card-head wr-card-head--row">
            <span>本周搜索标签</span>
          </div>
          <p class="wr-chart-sub wr-chart-sub--tight">当前账号本周在搜索中触达的标签分布</p>
          <div v-if="userSearchTagsFromApi.length && tagPieTotalCount > 0" class="wr-pie-body">
            <div class="wr-pie-left">
              <div class="wr-pie-visual">
                <div
                  class="wr-donut-wrap"
                  :style="pieDonutHoleVarStyle"
                  role="img"
                  aria-label="本周搜索标签占比，点击扇区在中心与下方列表查看次数"
                >
                  <svg
                    class="wr-pie-svg"
                    viewBox="0 0 100 100"
                    xmlns="http://www.w3.org/2000/svg"
                    focusable="false"
                  >
                    <g
                      v-for="slice in tagPieSliceRanges"
                      :key="slice.name"
                      class="wr-pie-slice-group"
                      :class="{ 'is-popped': pieSelection && pieSelection.name === slice.name }"
                      :transform="slicePopTransform(slice)"
                    >
                      <path
                        :d="slice.pathD"
                        :fill="slice.color"
                        :fill-rule="slice.isFullRing ? 'evenodd' : 'nonzero'"
                        class="wr-pie-slice-path"
                        stroke="#fff"
                        stroke-width="0.5"
                        vector-effect="non-scaling-stroke"
                        @click.stop="selectPieTag(slice, { scrollLegend: true })"
                      />
                    </g>
                  </svg>
                  <div class="wr-donut-hole">
                    <template v-if="pieSelection">
                      <span class="wr-donut-hole__n">{{ pieSelection.count }}</span>
                      <span class="wr-donut-hole__u">次</span>
                    </template>
                    <span v-else class="wr-donut-hint">点击扇区</span>
                  </div>
                </div>
              </div>
            </div>
            <ul ref="tagPieLegend" class="wr-legend wr-legend--scroll">
              <li
                v-for="item in tagPieLegendItems"
                :key="item.name"
                class="wr-legend-row"
                :class="{ 'is-active': pieSelection && pieSelection.name === item.name }"
                role="button"
                tabindex="0"
                @click="selectPieTag(item)"
                @keydown.enter.prevent="selectPieTag(item)"
              >
                <span class="wr-dot" :style="{ background: item.color }"></span>
                <span class="wr-legend-name">#{{ item.name }}</span>
                <strong>{{ item.count }} 次</strong>
              </li>
            </ul>
          </div>
          <div v-else-if="!pageLoading" class="wr-empty wr-empty--pie">
            <template v-if="!userId">请先登录后查看。</template>
            <template v-else>本周暂无标签搜索数据。</template>
          </div>
        </div>

        <div class="wr-card wr-card--chart wr-card--tags">
          <div class="wr-card-head">标签与搜索</div>

          <div class="wr-tag-block">
            <div class="wr-tag-block__title">本周全站搜索热门标签 TOP5</div>
            <p class="wr-chart-sub">当周全站用户搜索命中次数最多的前五名</p>
            <div v-if="globalHotBarTags.length" class="wr-bar-list">
              <div v-for="(t, index) in globalHotBarTags" :key="t.name" class="wr-bar-row">
                <span class="wr-bar-name">#{{ t.name }}</span>
                <div class="wr-bar-track">
                  <div
                    class="wr-bar-fill"
                    :style="{ width: t.pct + '%', background: barColors[index % barColors.length] }"
                  ></div>
                </div>
                <span class="wr-bar-count">{{ t.count }} 次</span>
              </div>
            </div>
            <div v-else-if="!pageLoading" class="wr-empty">暂无 TOP5 标签数据。</div>
          </div>
        </div>
      </div>

      <!-- MBTI：跳转子页展示 -->
      <div class="wr-card wr-card--mbti">
        <div class="wr-card-head wr-card-head--row">
          <span>内容偏好人格</span>
        </div>
        <p class="wr-chart-sub wr-chart-sub--tight">
          查看基于当前账号每周感兴趣的视频标签解读
        </p>
        <button type="button" class="wr-mbti-trigger" @click="goMbtiPage">
          查看我的 MBTI
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { getWeekDateRangeDisplay, getMondayOfWeek, getIsoWeekId } from '@/utils/weekReportStats'
import { fetchWeeklyStatistics, getWeekStatisticsRange } from '@/api/weekReport'

export default {
  name: 'WeekReport',
  data() {
    return {
      weekPickerValue: null,
      weekPickerOptions: {
        firstDayOfWeek: 1
      },
      /** keep-alive 首次进入：mounted 已加载，跳过同一次 activated */
      weekReportSkipFirstActivated: true,
      /** 进入页时右侧滑入动画 */
      pageEnterAnimating: false,
      /** 当周汇总（KPI、环比、标签等） */
      overview: null,
      userId: null,
      pageLoading: false,
      userSearchTagsFromApi: [],
      globalHotTagsFromApi: [],
      /** 饼图/列表选中的标签（中心数字与列表高亮） */
      pieSelection: null
    }
  },
  computed: {
    weekRangeLabel() {
      const d = this.weekPickerValue || new Date()
      return getWeekDateRangeDisplay(d).label
    },
    effectiveStats() {
      if (!this.overview) {
        return {
          weekId: '',
          videoCount: 0,
          timeSavedMinutes: 0
        }
      }
      return {
        weekId: this.overview.weekId,
        videoCount: this.overview.videoCount,
        timeSavedMinutes: this.overview.timeSavedMinutes
      }
    },
    lastWeek() {
      return this.overview && this.overview.lastWeek ? this.overview.lastWeek : null
    },
    hoursSaved() {
      const h = (this.effectiveStats.timeSavedMinutes || 0) / 60
      return h.toFixed(2)
    },
    avgDailyMinutes() {
      const m = (this.effectiveStats.timeSavedMinutes || 0) / 7
      return Math.round(m)
    },
    videoGrowPercent() {
      if (!this.lastWeek) return null
      const cur = this.effectiveStats.videoCount || 0
      const last = this.lastWeek.videoCount || 0
      if (last === 0) return cur === 0 ? null : 100
      return Math.round(((cur - last) / last) * 1000) / 10
    },
    timeComparePercent() {
      if (!this.lastWeek) return null
      if (this.lastWeek.timeSavedMinutes == null) return null
      const cur = this.effectiveStats.timeSavedMinutes || 0
      const last = this.lastWeek.timeSavedMinutes || 0
      if (last === 0) return cur === 0 ? null : 100
      return Math.round(((cur - last) / last) * 1000) / 10
    },
    tagPieTotalCount() {
      const tags = this.userSearchTagsFromApi
      if (!Array.isArray(tags) || !tags.length) return 0
      return tags.reduce((s, t) => s + (Number(t.count) || 0), 0)
    },
    tagPieLegendItems() {
      const tags = this.userSearchTagsFromApi
      const colors = this.barColors
      if (!Array.isArray(tags)) return []
      return tags
        .filter((t) => (Number(t.count) || 0) > 0)
        .map((t, i) => ({
          name: t.name,
          count: Number(t.count) || 0,
          color: colors[i % colors.length]
        }))
    },
    /**
     * 饼图环几何：原 R38/r22（环宽 16 viewBox），在 184px 直径下环宽再 +5px。
     * 5px → 5/184*100 viewBox，与 pieDonutHoleVarStyle 内圈比例一致。
     */
    pieDonutRadii() {
      const cx = 50
      const cy = 50
      const R = 38
      const origRingVb = 16
      const donutRefPx = 184
      const addRingPx = 5
      const addVb = (addRingPx * 100) / donutRefPx
      const r = R - origRingVb - addVb
      return { cx, cy, R, r }
    },
    pieDonutHoleVarStyle() {
      const { r } = this.pieDonutRadii
      return {
        '--wr-pie-inner-diam-pct': `${2 * r}%`
      }
    },
    /** 扇区角度 + SVG path（0°=正上方，顺时针） */
    tagPieSliceRanges() {
      const items = this.tagPieLegendItems
      const total = this.tagPieTotalCount
      if (!items.length || !total) return []
      let acc = 0
      const { cx, cy, R, r } = this.pieDonutRadii
      return items.map((item) => {
        const sweep = (item.count / total) * 360
        const startDeg = acc
        acc += sweep
        const endDeg = acc
        const isFullRing = items.length === 1 && sweep >= 359.5
        const pathD = isFullRing
          ? this.fullDonutRingPath(cx, cy, R, r)
          : this.donutSectorPath(cx, cy, R, r, startDeg, endDeg)
        return {
          name: item.name,
          count: item.count,
          color: item.color,
          startDeg,
          endDeg,
          pathD,
          isFullRing
        }
      })
    },
    globalHotBarTags() {
      const list = this.globalHotTagsFromApi
      const max = list.length ? Math.max(...list.map((x) => x.count), 1) : 1
      return list.map((x) => ({
        ...x,
        pct: Math.round((x.count / max) * 100)
      }))
    },
    barColors() {
      return ['#1D5D9B', '#42A5F5', '#26A69A', '#66BB6A', '#FFA726', '#AB47BC', '#78909C', '#8D6E63']
    },
  },
  created() {
    this.weekPickerValue = getMondayOfWeek(new Date())
  },
  mounted() {
    this.loadWeekReportPage()
  },
  activated() {
    this.runPageEnterAnimation()
    if (this.weekReportSkipFirstActivated) {
      this.weekReportSkipFirstActivated = false
      return
    }
    this.loadWeekReportPage()
  },
  methods: {
    runPageEnterAnimation() {
      this.pageEnterAnimating = false
      this.$nextTick(() => {
        this.pageEnterAnimating = true
        window.setTimeout(() => {
          this.pageEnterAnimating = false
        }, 400)
      })
    },
    goMbtiPage() {
      this.$router.push({ name: 'WeekReportMbti' })
    },
    goBack() {
      if (typeof window !== 'undefined' && window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/profile')
      }
    },
    onWeekPickerChange(val) {
      if (!val) {
        this.weekPickerValue = getMondayOfWeek(new Date())
      } else {
        this.weekPickerValue = getMondayOfWeek(val)
      }
      this.loadWeekReportPage()
    },
    async loadWeekReportPage() {
      const monday = getMondayOfWeek(this.weekPickerValue || new Date())
      const weekId = getIsoWeekId(monday)
      const { startDate, endDate } = getWeekStatisticsRange(monday)
      const uid = typeof localStorage !== 'undefined' ? localStorage.getItem('userId') : null
      this.userId = uid
      this.pageLoading = true
      this.overview = null
      this.userSearchTagsFromApi = []
      this.globalHotTagsFromApi = []
      this.pieSelection = null
      try {
        const pack = await fetchWeeklyStatistics(uid, startDate, endDate, weekId)
        this.overview = pack.overview
        this.userSearchTagsFromApi = Array.isArray(pack.userLabelTags) ? pack.userLabelTags : []
        this.globalHotTagsFromApi = Array.isArray(pack.hotTags) ? pack.hotTags : []
      } catch (e) {
        console.error(e)
        if (this.$message) {
          this.$message.error(getApiErrorMessage(e, '周报数据加载失败'))
        }
      } finally {
        this.pageLoading = false
      }
    },
    formatInt(n) {
      const x = Number(n) || 0
      return x.toLocaleString('zh-CN')
    },
    selectPieTag(item, opts) {
      if (!item || item.name == null) return
      this.pieSelection = {
        name: String(item.name),
        count: Number(item.count) || 0,
        color: item.color || '#1d5d9b'
      }
      if (opts && opts.scrollLegend) {
        this.$nextTick(() => {
          this.scrollActivePieLegendRowIntoView()
        })
      }
    },
    /** 从饼图选中时：仅滚动图例列表容器，不带动外层页面 */
    scrollActivePieLegendRowIntoView() {
      const root = this.$refs.tagPieLegend
      if (!root) return
      const row = root.querySelector('.wr-legend-row.is-active')
      if (!row) return
      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const nextTop =
        row.offsetTop - (root.clientHeight - row.offsetHeight) / 2
      const maxTop = Math.max(0, root.scrollHeight - root.clientHeight)
      const top = Math.max(0, Math.min(nextTop, maxTop))
      if (reduceMotion) {
        root.scrollTop = top
      } else {
        root.scrollTo({ top, behavior: 'smooth' })
      }
    },
    /** SVG 环形扇区：角度从正上方顺时针 */
    donutSectorPath(cx, cy, R, rin, startDeg, endDeg) {
      const t1 = (startDeg * Math.PI) / 180
      const t2 = (endDeg * Math.PI) / 180
      const x1o = cx + R * Math.sin(t1)
      const y1o = cy - R * Math.cos(t1)
      const x2o = cx + R * Math.sin(t2)
      const y2o = cy - R * Math.cos(t2)
      const x1i = cx + rin * Math.sin(t1)
      const y1i = cy - rin * Math.cos(t1)
      const x2i = cx + rin * Math.sin(t2)
      const y2i = cy - rin * Math.cos(t2)
      const span = endDeg - startDeg
      const large = span > 180 ? 1 : 0
      return `M ${x1o} ${y1o} A ${R} ${R} 0 ${large} 1 ${x2o} ${y2o} L ${x2i} ${y2i} A ${rin} ${rin} 0 ${large} 0 ${x1i} ${y1i} Z`
    },
    /** 整圈环形（仅一段占满 100% 时） */
    fullDonutRingPath(cx, cy, R, rin) {
      return `
        M ${cx + R} ${cy}
        A ${R} ${R} 0 1 1 ${cx - R} ${cy}
        A ${R} ${R} 0 1 1 ${cx + R} ${cy}
        M ${cx + rin} ${cy}
        A ${rin} ${rin} 0 1 0 ${cx - rin} ${cy}
        A ${rin} ${rin} 0 1 0 ${cx + rin} ${cy}
      `
        .replace(/\s+/g, ' ')
        .trim()
    },
    /** 选中扇区沿径向「浮起」 */
    slicePopTransform(slice) {
      if (!slice || !this.pieSelection || this.pieSelection.name !== slice.name) {
        return 'translate(0,0)'
      }
      const mid = (slice.startDeg + slice.endDeg) / 2
      const rad = (mid * Math.PI) / 180
      const k = 3.2
      const dx = k * Math.sin(rad)
      const dy = -k * Math.cos(rad)
      return `translate(${dx}, ${dy})`
    }
  }
}
</script>

<style scoped>
.week-report-page.wr-app {
  height: 100%;
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;
  will-change: transform;
}

.week-report-page.wr-app.wr-page--loading {
  overflow: hidden;
}

.week-report-page.wr-app.wr-page--enter {
  animation: wrEnterFromRight 0.38s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
}

@keyframes wrEnterFromRight {
  from {
    transform: translateX(100%);
    opacity: 0.96;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.wr-top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;
  flex-shrink: 0;
  padding: 16px 20px;
  padding-top: calc(16px + env(safe-area-inset-top, 0px));
  padding-left: calc(12px + env(safe-area-inset-left, 0px));
  padding-right: calc(20px + env(safe-area-inset-right, 0px));
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: relative;
  z-index: 20;
}

.wr-top-left {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.wr-back-btn {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  padding: 0;
  margin: 0 4px 0 -4px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: rgba(0, 0, 0, 0.75);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
}

.wr-back-btn:hover {
  background: rgba(29, 93, 155, 0.08);
  color: #1d5d9b;
}

.wr-back-btn i {
  font-size: 20px;
}

.wr-top-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  min-width: 0;
}

.wr-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
  letter-spacing: -0.02em;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wr-date-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
}

.wr-week-pill {
  position: relative;
  min-width: 0;
  max-width: 100%;
  min-height: 40px;
  box-sizing: border-box;
}

.wr-week-pill__icon {
  position: relative;
  z-index: 1;
  color: #1d5d9b;
  pointer-events: none;
}

.wr-week-pill__label {
  position: relative;
  z-index: 1;
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: min(200px, 42vw);
}

.wr-week-picker {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  margin: 0;
}

.wr-week-picker >>> .el-input__inner {
  opacity: 0;
  cursor: pointer;
  height: 100%;
  min-height: 40px;
  padding: 0;
}

.wr-week-picker >>> .el-input__prefix,
.wr-week-picker >>> .el-input__suffix {
  display: none;
}

.wr-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 20px 16px;
  padding-left: calc(16px + env(safe-area-inset-left, 0px));
  padding-right: calc(16px + env(safe-area-inset-right, 0px));
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
}

.wr-content.is-loading {
  overflow: hidden;
  touch-action: none;
}

/* KPI 行：视频数 + 节省时间（三项合一） */
.wr-kpi-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
  gap: 16px;
  margin-bottom: 16px;
  align-items: stretch;
}

@media (max-width: 1024px) {
  .wr-kpi-row {
    grid-template-columns: 1fr;
  }
}

.wr-card--time-bundle .wr-card-head {
  font-weight: 500;
  color: #333;
}

.wr-time-bundle {
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 0;
  margin-top: 4px;
}

.wr-time-col {
  flex: 1;
  min-width: 0;
  padding: 0 14px;
  text-align: center;
  border-left: 1px solid #f0f0f0;
}

.wr-time-col:first-child {
  padding-left: 0;
  border-left: none;
}

.wr-time-label {
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
  line-height: 1.3;
}

.wr-time-value {
  font-size: 26px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.wr-time-value--compare {
  font-size: 22px;
  color: #43a047;
}

.wr-time-value--compare.is-down {
  color: #e53935;
}

.wr-time-value--empty {
  font-size: 22px;
  color: #bfbfbf;
  font-weight: 600;
}

.wr-time-unit {
  font-size: 11px;
  color: #aaa;
  margin-top: 6px;
}

@media (max-width: 480px) {
  .wr-time-bundle {
    flex-wrap: wrap;
    row-gap: 16px;
  }

  .wr-time-col {
    flex: 1 1 45%;
    border-left: none;
    padding: 0 8px;
  }

  .wr-time-col:nth-child(2) {
    border-left: 1px solid #f0f0f0;
  }

  .wr-time-col:nth-child(3) {
    flex-basis: 100%;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
    border-left: none;
  }
}

.wr-card {
  background: #fff;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  border: 1px solid #eee;
}

.wr-card-head {
  font-size: 13px;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.wr-card-head--row {
  justify-content: space-between;
}

.wr-sub-hint {
  font-size: 12px;
  color: #999;
  font-weight: 400;
}

.wr-icon-hint {
  font-size: 14px;
  color: #bfbfbf;
  cursor: help;
}

.wr-card--hero .wr-card-head {
  font-weight: 500;
  color: #333;
}

.wr-hero-metric {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}

.wr-hero-num {
  font-size: 36px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.1;
  letter-spacing: -0.03em;
}

.wr-grow {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  background: #e8f5e9;
  color: #4caf50;
}

.wr-grow.down {
  background: #ffebee;
  color: #e53935;
}

/* 图表行 */
.wr-charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 900px) {
  .wr-charts-row {
    grid-template-columns: 1fr;
  }
}

/* MBTI 人格分析 */
.wr-card--mbti {
  margin-top: 16px;
}

.wr-mbti-trigger {
  display: block;
  width: 100%;
  margin: 0 0 4px;
  padding: 12px 16px;
  border: 1px solid #93c5fd;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #1e3a8a;
  cursor: pointer;
  background: linear-gradient(120deg, #ffffff 0%, #eff6ff 42%, #dbeafe 100%);
  box-shadow: 0 6px 18px rgba(37, 99, 235, 0.18);
  -webkit-tap-highlight-color: transparent;
  transition:
    transform 0.15s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease;
}

.wr-mbti-trigger:hover {
  border-color: #60a5fa;
  background: linear-gradient(120deg, #ffffff 0%, #e0f2fe 48%, #bfdbfe 100%);
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.24);
}

.wr-mbti-trigger:active {
  transform: scale(0.98);
}

.wr-card--chart {
  min-height: 280px;
}

.wr-card--tag-pie {
  transition:
    transform 0.28s ease,
    box-shadow 0.28s ease;
}

.wr-card--tag-pie:hover {
  transform: translateY(-5px);
  box-shadow: 0 14px 32px rgba(29, 93, 155, 0.12);
}

.wr-card--tags {
  min-height: auto;
}

.wr-tag-block {
  margin-bottom: 24px;
}

.wr-tag-block:last-child {
  margin-bottom: 0;
}

.wr-tag-block__title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.wr-tag-block .wr-chart-sub {
  margin: 0 0 12px 0;
}

.wr-chart-sub {
  margin: -4px 0 16px 0;
  font-size: 12px;
  color: #999;
}

.wr-chart-sub--tight {
  margin: -6px 0 14px;
}

.wr-pie-body {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 16px;
  flex-wrap: nowrap;
  transition: transform 0.28s ease;
}

.wr-pie-left {
  flex: 0 1 auto;
  min-width: 0;
}

.wr-pie-visual {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 0;
}

.wr-card--tag-pie .wr-empty--pie {
  padding: 20px 0 8px;
}

.wr-donut-wrap {
  position: relative;
  width: 184px;
  height: 184px;
  flex-shrink: 0;
  cursor: pointer;
  transition:
    transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1),
    filter 0.28s ease;
}

.wr-pie-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
}

.wr-pie-slice-group {
  transition: filter 0.22s ease;
}

.wr-pie-slice-group.is-popped {
  filter: drop-shadow(0 -4px 8px rgba(29, 93, 155, 0.28))
    drop-shadow(0 2px 4px rgba(0, 0, 0, 0.06));
}

.wr-pie-slice-path {
  cursor: pointer;
}

.wr-pie-body:hover .wr-donut-wrap {
  transform: scale(1.025);
  filter: drop-shadow(0 6px 12px rgba(29, 93, 155, 0.14));
}

.wr-donut {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.wr-donut-hole {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: var(--wr-pie-inner-diam-pct);
  height: var(--wr-pie-inner-diam-pct);
  border-radius: 50%;
  background: #fff;
  box-shadow: inset 0 0 0 1px #f0f0f0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  padding: 4px;
  box-sizing: border-box;
}

.wr-donut-hole__n {
  font-size: 28px;
  font-weight: 700;
  color: #1d5d9b;
  line-height: 1;
}

.wr-donut-hole__u {
  margin-top: 2px;
  font-size: 11px;
  color: #8a9aba;
  line-height: 1;
}

.wr-donut-hint {
  font-size: 10px;
  line-height: 1.2;
  color: #a8b4cc;
  text-align: center;
  user-select: none;
}

.wr-legend {
  position: relative;
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  font-size: 13px;
  color: #666;
}

.wr-legend--scroll {
  max-height: 200px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-right: 4px;
}

.wr-legend li {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.wr-legend-row {
  cursor: pointer;
  border-radius: 8px;
  margin-left: -6px;
  margin-right: -4px;
  padding: 6px 6px 6px 8px;
  transition: background 0.2s ease;
}

.wr-legend-row:hover {
  background: rgba(29, 93, 155, 0.06);
}

.wr-legend-row:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(29, 93, 155, 0.35);
}

.wr-legend-row.is-active {
  background: rgba(29, 93, 155, 0.1);
  box-shadow: inset 0 0 0 1px rgba(29, 93, 155, 0.22);
  scroll-margin-block: 8px;
}

.wr-legend-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wr-legend strong {
  flex-shrink: 0;
  color: #333;
}

.wr-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* 横向条形图 */
.wr-bar-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.wr-bar-row {
  display: grid;
  grid-template-columns: 100px 1fr 72px;
  align-items: center;
  gap: 10px;
  font-size: 13px;
}

@media (max-width: 500px) {
  .wr-bar-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }
  .wr-bar-count {
    text-align: left;
  }
}

.wr-bar-name {
  color: #333;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wr-bar-track {
  height: 10px;
  background: #f0f0f0;
  border-radius: 999px;
  overflow: hidden;
}

.wr-bar-fill {
  height: 100%;
  border-radius: 999px;
  min-width: 4px;
  transition: width 0.35s ease;
}

.wr-bar-count {
  font-size: 12px;
  color: #888;
  text-align: right;
}

.wr-empty {
  font-size: 13px;
  color: #999;
  padding: 24px 0;
  text-align: center;
}

/* —— App / 窄屏 WebView —— */
@media screen and (max-width: 768px) {
  .wr-top-bar {
    gap: 8px;
  }

  .wr-date-pill.wr-week-pill {
    min-height: 44px;
    padding: 6px 10px;
  }

  .wr-week-pill__label {
    max-width: min(160px, 38vw);
  }

  .wr-title {
    font-size: 18px;
  }

  .wr-content {
    padding-top: 14px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  }

  .wr-card {
    padding: 14px 14px;
    border-radius: 12px;
  }

  .wr-hero-num {
    font-size: 30px;
  }

  .wr-grow {
    font-size: 12px;
    padding: 4px 8px;
  }

  .wr-time-value {
    font-size: 22px;
  }

  .wr-time-value--compare,
  .wr-time-value--empty {
    font-size: 20px;
  }

  .wr-card--chart {
    min-height: 0;
  }

  .wr-pie-body {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .wr-pie-visual {
    flex-direction: column;
    width: 100%;
    max-width: 320px;
  }

  .wr-legend {
    width: 100%;
    max-height: 180px;
    text-align: left;
  }

  .wr-legend li {
    justify-content: flex-start;
  }

  .wr-donut-wrap {
    width: 158px;
    height: 158px;
  }

  .wr-donut-hole {
    width: var(--wr-pie-inner-diam-pct);
    height: var(--wr-pie-inner-diam-pct);
  }

  .wr-donut-hole__n {
    font-size: 24px;
  }
}

@media screen and (max-width: 380px) {
  .wr-title {
    font-size: 17px;
  }

  .wr-hero-num {
    font-size: 26px;
  }

  .wr-time-value {
    font-size: 20px;
  }
}
</style>

<style>
/* 挂载到 body 的周选择面板，避免 App WebView 内被 overflow 裁剪并盖住底栏 */
.wr-week-picker-popper.el-picker-panel {
  z-index: 5000 !important;
}
</style>
